import React, { useRef, useState } from 'react';
import { Button } from 'components/buttons';
import { FileAudio, FileCheck2 } from 'lucide-react';

import { useTranslation } from 'react-i18next';

interface AudioUploaderProps {
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  className?: string;
}

export const AudioFileSelector: React.FC<AudioUploaderProps> = ({
  onFileSelect,
  onRemove,
  accept = 'audio/*,.mp3,.wav,.m4a,.aac,.ogg,.webm',
  maxSize = 50 * 1024 * 1024, // 50MB default
  className = '',
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setError('');

    if (!file) return;

    const isValidType =
      file.type.startsWith('audio/') ||
      ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.webm'].some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );

    if (!isValidType) {
      setError(t('errors.invalidAudioFileType'));
      return;
    }

    if (file.size > maxSize) {
      setError(t('errors.fileTooLarge', { maxSize: Math.round(maxSize / (1024 * 1024)) }));
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onRemove?.();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`audio-uploader ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        aria-label={t('recording.selectAudioFile')}
      />

      {!selectedFile ? (
        <div className="upload-area border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <div className="space-y-4">
            <div className="text-gray-500 flex justify-center items-center">
              <FileAudio size={40} />
            </div>
            <div>
              <Button type="button" onClick={handleButtonClick} fullWidth className="mb-2">
                {t('recording.selectAudioFile')}
              </Button>
              <p className="text-sm text-gray-500">
                {t('recording.supportedFormats')}: MP3, WAV, M4A, AAC, OGG, WebM
              </p>
              <p className="text-xs text-gray-400">
                {t('recording.maxFileSize', { maxSize: Math.round(maxSize / (1024 * 1024)) })}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="selected-file bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <FileCheck2 size={40} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveFile}
              className="text-red-600 hover:text-red-700"
            >
              {t('general.remove')}
            </Button>
          </div>
        </div>
      )}

      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
    </div>
  );
};

export default AudioFileSelector;
